import React, { useState } from "react";
import { Apple, Check, X } from "lucide-react";

/**
 * Compact apples-to-apples badge.
 * Shows score + click-to-reveal popover with matched / missing items + verdict.
 */
export default function ApplesBadge({ bid, compact = false }) {
  const [open, setOpen] = useState(false);
  const score = bid?.apples_score;
  if (score == null) return null;

  const tone =
    score >= 85 ? "text-green-400 border-green-400/40 bg-green-400/5"
    : score >= 60 ? "text-brand border-brand/40 bg-brand/5"
    : "text-amber-400 border-amber-400/40 bg-amber-400/5";

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border font-mono text-xs ${tone} hover:bg-opacity-20 transition-colors`}
        data-testid={`apples-badge-${bid.bid_id || ""}`}
        title="Apple-to-apple completeness check"
      >
        <Apple className="w-3.5 h-3.5" />
        <span className="font-bold">{score}%</span>
        {!compact && <span className="hidden sm:inline tracking-widest uppercase text-[10px]">match</span>}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-72 z-30 bg-black border border-[#262626] rounded-lg shadow-2xl p-4 text-left"
          data-testid="apples-popover"
        >
          <div className="flex items-center justify-between mb-3">
            <p className="overline">Apple-to-apple check</p>
            <span className={`font-mono text-sm font-bold ${tone.split(" ")[0]}`}>{score}%</span>
          </div>
          {bid.apples_verdict && (
            <p className="text-xs text-[#d4d4d4] leading-relaxed mb-3">{bid.apples_verdict}</p>
          )}
          {bid.apples_matched?.length > 0 && (
            <div className="mb-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-green-400 mb-1.5">Matched</p>
              <ul className="space-y-1">
                {bid.apples_matched.map((m) => (
                  <li key={m} className="flex items-center gap-1.5 text-xs text-[#d4d4d4]">
                    <Check className="w-3 h-3 text-green-400 flex-shrink-0" /> {m}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {bid.apples_missing?.length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-amber-400 mb-1.5">Gaps</p>
              <ul className="space-y-1">
                {bid.apples_missing.map((m) => (
                  <li key={m} className="flex items-center gap-1.5 text-xs text-[#d4d4d4]">
                    <X className="w-3 h-3 text-amber-400 flex-shrink-0" /> {m}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
